/**
 * FeedbackModal — quiet feedback channel exposed via the Tutor Buddy widget.
 *
 * The student types whatever they want about the platform; the buddy acknowledges
 * with a warm thank-you. Async LLM analysis (category/severity/sentiment/suggestion)
 * happens server-side; we don't wait for it on submit.
 *
 * UX rules:
 *  - All tiers can submit (even Free Basic — every voice matters).
 *  - Optional 1–5 star rating.
 *  - 4000-char limit with live counter.
 *  - On success: replace form with the warm AI-ack message + 'Done' button.
 */
import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageCircleHeart, Star, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { apiPost } from '@/lib/api';
import { BuddyAvatar, BUDDY_ROSTER } from '@/components/BuddyRoster';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const MAX_LEN = 4000;

export default function FeedbackModal({ open, onOpenChange, initialMessage = '' }) {
  const { user } = useAuth();
  const buddyId = (user?.preferred_buddy && BUDDY_ROSTER[user.preferred_buddy]) ? user.preferred_buddy : 'chirpy';
  const buddy = BUDDY_ROSTER[buddyId];

  const [message, setMessage] = useState(initialMessage);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [ack, setAck] = useState(null);
  const taRef = useRef(null);

  // Reset form whenever the modal closes. When re-opening with a new
  // initialMessage (e.g. Gamify "Share what you'd love to play" pre-seeds
  // the subject+topic context), rehydrate the textarea.
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => { setMessage(''); setRating(0); setHoverRating(0); setAck(null); setSubmitting(false); }, 220);
      return () => clearTimeout(t);
    } else {
      // Seed with the caller-supplied context so the student sees "we
      // already know what chapter you're on — just add your idea".
      setMessage(initialMessage || '');
      // focus the textarea after the dialog mounts
      const t = setTimeout(() => taRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [open]);

  const submit = async () => {
    const text = message.trim();
    if (!text) { toast.error("Please write a few words first."); return; }
    if (text.length > MAX_LEN) { toast.error(`That's a bit long — please trim to under ${MAX_LEN} characters.`); return; }
    setSubmitting(true);
    try {
      const res = await apiPost('/feedback', {
        message: text,
        page_context: typeof window !== 'undefined' ? window.location.pathname : '',
        rating: rating > 0 ? rating : null,
      });
      setAck(res?.ack_message || "Thank you — passing this on to our team. ❤");
    } catch (err) {
      toast.error((err?.message || '').replace(/^HTTP \d+:\s*/, '') || "Couldn't send your feedback. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = MAX_LEN - message.length;
  const tooLong = remaining < 0;
  const charCounterColor = remaining < 100 ? 'text-amber-600' : remaining < 0 ? 'text-rose-600' : 'text-slate-400';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="feedback-modal">
        {!ack ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl overflow-hidden bg-white shadow ring-1 ring-rose-100 flex-shrink-0">
                  <BuddyAvatar buddyId={buddyId} size={40} mood="curious" />
                </div>
                <div>
                  <DialogTitle className="font-display text-lg flex items-center gap-2">
                    <MessageCircleHeart className="w-4 h-4 text-rose-500" />
                    Share feedback with {buddy?.name || 'me'}
                  </DialogTitle>
                  <DialogDescription className="text-xs font-body text-slate-500 mt-0.5">
                    Suggestions, complaints, bugs, ideas — everything helps us get better.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-4 mt-2">
              <div>
                <Textarea
                  ref={taRef}
                  data-testid="feedback-message-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={"What's on your mind? e.g. 'The quiz is too long' or 'Please add Hindi support' or 'I love Chirpy ❤'"}
                  rows={6}
                  maxLength={MAX_LEN + 200}
                  disabled={submitting}
                  className="resize-none font-body text-sm border-slate-200 focus:border-rose-300 focus:ring-rose-200"
                />
                <div className="flex justify-between items-center mt-1">
                  <p className="text-[11px] text-slate-400 font-body">
                    Your name and class are shared with the team so they can follow up.
                  </p>
                  <span className={`text-[11px] font-mono ${charCounterColor}`} data-testid="feedback-char-counter">
                    {remaining}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-xs font-heading text-slate-600 mr-1">How would you rate us?</span>
                <div className="flex items-center gap-1" onMouseLeave={() => setHoverRating(0)} data-testid="feedback-rating-row">
                  {[1, 2, 3, 4, 5].map(n => {
                    const filled = (hoverRating || rating) >= n;
                    return (
                      <button key={n} type="button" data-testid={`feedback-star-${n}`}
                        onClick={() => setRating(rating === n ? 0 : n)}
                        onMouseEnter={() => setHoverRating(n)}
                        disabled={submitting}
                        className="p-0.5 transition-transform hover:scale-110">
                        <Star className={`w-5 h-5 ${filled ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
                      </button>
                    );
                  })}
                </div>
                {rating > 0 && (
                  <button type="button" onClick={() => setRating(0)} disabled={submitting}
                    className="ml-auto text-[10px] text-slate-400 hover:text-slate-600 font-body underline" data-testid="feedback-rating-clear">
                    Clear
                  </button>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}
                  data-testid="feedback-cancel-btn" className="font-heading text-xs">
                  Cancel
                </Button>
                <Button onClick={submit} disabled={submitting || !message.trim() || tooLong}
                  data-testid="feedback-submit-btn"
                  className="font-heading text-xs bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white">
                  {submitting ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sending...</>
                  ) : (
                    <><Send className="w-3.5 h-3.5 mr-1.5" />Send feedback</>
                  )}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-6 px-4" data-testid="feedback-ack">
            <div className="w-20 h-20 rounded-2xl overflow-hidden bg-white shadow-md mx-auto mb-4 ring-2 ring-rose-100">
              <BuddyAvatar buddyId={buddyId} size={80} mood="happy" />
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-heading mb-3">
              <CheckCircle2 className="w-3.5 h-3.5" /> Sent
            </div>
            <p className="text-sm font-body text-slate-700 leading-relaxed max-w-sm mx-auto">
              {ack}
            </p>
            <Button onClick={() => onOpenChange(false)} data-testid="feedback-done-btn"
              className="mt-5 font-heading text-xs bg-rose-500 hover:bg-rose-600 text-white">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
